'use client';

import { ReactNode, useState } from 'react';
import { motion } from 'motion/react';
import { DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { HeroConsolidatedProps } from '../types';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import EmailCapture from 'components/shared/EmailCapture/EmailCapture';

interface FocusDisclaimersProps {
  focusDisclaimers: DocumentWithEmbeddedEntries;
  disclaimers?: DocumentWithEmbeddedEntries;
  sys?: HeroConsolidatedProps['sys'];
  buttonText: string;
  baseEnrollmentUrl: string;
  children?: ReactNode;
}

const TRANSITION = { duration: 0.2, ease: 'easeInOut' as const };

export const FocusDisclaimers = ({
  focusDisclaimers,
  disclaimers,
  sys,
  buttonText,
  baseEnrollmentUrl,
  children,
}: FocusDisclaimersProps) => {
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  // Tracks whether focusDisclaimers is in the "above" position.
  // On focus: flips immediately so focusDisclaimers moves above before fading in.
  // On blur: stays above until the fade-out animation completes, then resets.
  const [isAbove, setIsAbove] = useState(false);

  const handleFocusChange = (focused: boolean) => {
    if (focused) setIsAbove(true);
    setIsEmailFocused(focused);
  };

  return (
    <div
      className="contents"
      onFocus={() => handleFocusChange(true)}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          handleFocusChange(false);
        }
      }}>
      <EmailCapture sys={sys} buttonText={buttonText} baseEnrollmentUrl={baseEnrollmentUrl} />
      {children}
      <div className="flex flex-col sm:mb-4">
        {/* Always in the document flow so the hero never changes height.
            CSS order swaps it above disclaimers when focused. */}
        <motion.div
          onMouseDown={e => e.preventDefault()}
          aria-hidden={!isEmailFocused}
          initial={{ opacity: 0 }}
          animate={{ opacity: isEmailFocused ? 1 : 0 }}
          transition={TRANSITION}
          onAnimationComplete={() => {
            if (!isEmailFocused) setIsAbove(false);
          }}
          style={{
            order: isAbove ? 0 : 1,
            pointerEvents: isEmailFocused ? 'auto' : 'none',
          }}
          className="hero-disclaimers font-sans text-legal flex flex-col gap-2 pb-2"
          {...liveUpdateAttrs(sys?.id, 'focusDisclaimers')}>
          <RichTextCopy copy={focusDisclaimers} ySpacing={'none'} />
        </motion.div>
        {disclaimers && (
          <motion.div
            onMouseDown={e => e.preventDefault()}
            layout="position"
            transition={TRANSITION}
            style={{ order: isAbove ? 1 : 0 }}
            className="hero-disclaimers font-sans text-legal flex flex-col gap-2"
            {...liveUpdateAttrs(sys?.id, 'disclaimers')}>
            <RichTextCopy copy={disclaimers} ySpacing={'none'} />
          </motion.div>
        )}
      </div>
    </div>
  );
};
